import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import CallTransfer from "../../CallTransfer/CallTransfer";
import SophiaReadyDialog from "../SophiaReadyDialog";
import { markSetupStep, fetchSetupScore } from "../../../lib/setupScore";
import { trackEvent } from "../../../lib/analytics";
import { getTriageDefaultsForCategory } from "../../../lib/triageDefaultsByCategory";
import {
  beginAddonSequence,
  clearAddonFlow,
  finishAddonFlow,
} from "../../../lib/addonIntroFlow";
import { calenderCheck } from "../../../Store/apiStore";
import decodeToken from "../../../lib/decodeToken";
import styles from "./TriageStep.module.css";

/*
 * Whether the user SAVED (1) or SKIPPED (0) triage on this run. Session-scoped
 * because it only has to survive the add-on side-trip remount, not the session.
 */
const TRIAGE_SAVED_FLAG = "goLive.triageSaved";

/*
 * "{agent} is live" dialog — SHOWN ONCE, EVER, per agent.
 *
 * It is a one-time go-live celebration: the user crosses Call Triage for the
 * first time and sees it. Every later pass through the wizard (editing triage
 * from /setup-ready, re-running go-live, a second visit) must go straight
 * through without the popup.
 *
 * localStorage, not sessionStorage — sessionStorage dies with the tab, which
 * would re-show the dialog on the user's next visit. Keyed per agent so a
 * second agent still gets its own first-time celebration.
 */
const readyDialogSeenKey = (agentId) =>
  `goLive.readyDialogSeen.${agentId || "unknown"}`;

const hasSeenReadyDialog = (agentId) => {
  try {
    return localStorage.getItem(readyDialogSeenKey(agentId)) === "1";
  } catch (_e) {
    // Private mode / storage disabled — fall back to SHOWING the dialog.
    // A duplicate celebration is a far smaller failure than silently
    // swallowing the only screen that offers the add-on side-trips.
    return false;
  }
};

const markReadyDialogSeen = (agentId) => {
  try {
    localStorage.setItem(readyDialogSeenKey(agentId), "1");
  } catch (_e) {
    /* best-effort — see hasSeenReadyDialog */
  }
};

/*
 * GoLive Step 3 wrapper — Call Triage Rules.
 *
 * Same underlying <CallTransfer/> as PostPurchaseSetup uses for its Step
 * 3, with two differences:
 *
 *   1. headings — passes Figma 13 spec copy ("Tell Sophia When to
 *      Transfer Calls" / sub-copy). PostPurchase omits and keeps the
 *      existing short "Call Triage" h2.
 *   2. NO auto-assign useEffect here — GoLiveWizard's shell already
 *      handles auto-assign at mount, BEFORE the user reaches this step.
 *      (PostPurchase's CallRoutingStep ran the cascade because that
 *      wizard puts CallRouting BEFORE the Number step.)
 *
 * Setup score `triageRulesSaved` (10 pts) fires on the same onWizardNext
 * wrap pattern as the PostPurchase wrapper.
 *
 * Default triage rule (1 row pre-filled with biz phone) is auto-created
 * by the existing CallTransfer empty-state useEffect — no extra wiring
 * needed here.
 */

export default function TriageStep({ onNext, onBack, agentCtx, useAddonIntro = false }) {
  const [ready, setReady] = useState(false);
  // SophiaReadyDialog opens on Save & Continue OR Skip — both buttons
  // converge here per spec. The dialog itself owns the final fork
  // (Continue Setup vs Go to Dashboard).
  const [dialogOpen, setDialogOpen] = useState(false);
  /*
   * Did the user actually SAVE triage rules, or Skip past them?
   * Both buttons open the same dialog, but the dialog's checklist must not
   * report a skipped step as configured. null = not decided yet (the
   * add-on reopen path restores it from sessionStorage below).
   */
  const [triageSaved, setTriageSaved] = useState(null);
  /*
   * Calendar Sync status for the dialog's "still to setup" pill row.
   * Fired once on TriageStep mount so the dialog already has a
   * resolved value when it opens (avoids a red ✕ → green ✓ flicker
   * if the API call resolves while the dialog is visible). Defaults
   * to false → dialog shows red ✕; flips to true if calenderCheck
   * confirms an existing connection.
   */
  const [calendarConnected, setCalendarConnected] = useState(false);
  /*
   * Setup-score steps, for the "is live" dialog's checklist.
   *
   * agentCtx is fetched when the WIZARD mounts, so by the time the user reaches
   * Triage it predates the number-assign and forwarding steps they just
   * completed — reading `agent.callForwarding` off it reported "not set up" for
   * forwarding the user had literally just finished.
   *
   * The score ledger is the live signal: ForwardingStep marks
   * `callForwardingDone` and the assign step marks `phoneNumberAssigned` as
   * they complete. Same source /setup-ready uses, so the two screens agree.
   */
  const [scoreSteps, setScoreSteps] = useState(null);
  // True when the dialog is being auto-reopened after an add-on side-trip
  // (spam / calendar) — used to NOT re-fire the go_live_complete key event
  // on the return trip (it already fired on the first open).
  const reopenedFromAddonRef = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Reopen the "Sophia is live" dialog when the user returns from an
  // add-on side-trip. handleDialogExitTo sets this flag in sessionStorage
  // just before navigating to /contacts or /connect-calender2; on the way
  // back GoLiveWizard resumes Triage and we pop the dialog open again so
  // the user can pick the other add-on too.
  useEffect(() => {
    let flagged = false;
    let savedFlag = null;
    try {
      flagged = sessionStorage.getItem("goLive.reopenReadyDialog") === "1";
      if (flagged) {
        // Restore the save-vs-skip answer BEFORE clearing it, so the
        // reopened dialog's checklist matches the first open instead of
        // silently falling back to "configured".
        const raw = sessionStorage.getItem(TRIAGE_SAVED_FLAG);
        if (raw === "1" || raw === "0") savedFlag = raw === "1";
        sessionStorage.removeItem("goLive.reopenReadyDialog");
        sessionStorage.removeItem("goLive.calendarAddonReturn");
      }
    } catch { /* private mode — flag just won't reopen, harmless */ }
    if (flagged) {
      reopenedFromAddonRef.current = true;
      if (savedFlag !== null) setTriageSaved(savedFlag);
      setDialogOpen(true);
    }
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const tokenStr = localStorage.getItem("token") || "";
        const userId = decodeToken(tokenStr)?.id;
        if (!userId) return;
        const res = await calenderCheck(userId);
        if (!alive) return;
        setCalendarConnected(!!res?.data?.connected);
      } catch (err) {
        // Soft-fail — defaulting to false (red ✕) is the safe
        // assumption when we can't determine connection state.
        console.warn(
          "[TriageStep] calenderCheck failed (defaulting to disconnected):",
          err?.message || err,
        );
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  /*
   * Pull the setup score whenever the dialog is about to be shown, so the
   * checklist reflects steps completed EARLIER IN THIS SAME WIZARD RUN.
   *
   * Keyed on `dialogOpen` rather than mount so the add-on side-trip reopen
   * also gets fresh values. Soft-fails to the durable agent fields below.
   */
  useEffect(() => {
    if (!dialogOpen) return;
    let alive = true;
    (async () => {
      try {
        const aId =
          agentCtx?.agent?.agent_id || agentCtx?.agentId || currentAgentId;
        if (!aId) return;
        const res = await fetchSetupScore(aId);
        if (alive && res?.steps) setScoreSteps(res.steps);
      } catch (err) {
        console.warn(
          "[TriageStep] setup-score fetch failed (falling back to agent fields):",
          err?.message || err,
        );
      }
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dialogOpen]);

  // GA4: SophiaReadyDialog ("[agent] is Live") shown = go-live complete.
  useEffect(() => {
    if (!dialogOpen) return;
    // Reopen after an add-on side-trip is not a fresh go-live — don't
    // double-count the key event.
    if (reopenedFromAddonRef.current) return;
    const pending = [!calendarConnected && "calendar", "spam"].filter(Boolean);
    trackEvent("go_live_complete", { pending_features: pending.join(",") });
  }, [dialogOpen, calendarConnected]);

  useEffect(() => {
    if (agentCtx?.loading) return;

    // Prime sessionStorage keys CallTransfer reads on mount, mirroring
    // the PostPurchase CallRoutingStep approach.
    const aId = agentCtx?.agentId || agentCtx?.agent?.agent_id || "";
    const bId = agentCtx?.business?.businessId || agentCtx?.business?.id || "";
    const lId = agentCtx?.agent?.llmId || "";

    if (aId) sessionStorage.setItem("SelectAgentId", aId);
    if (bId) sessionStorage.setItem("SelectAgentBusinessId", bId);
    if (lId) sessionStorage.setItem("llmId", lId);

    sessionStorage.setItem(
      "agentDetails",
      JSON.stringify({
        agent: agentCtx?.agent || {},
        business: agentCtx?.business || {},
      }),
    );

    setReady(true);
  }, [agentCtx]);

  /*
   * Declared ABOVE the `!ready` early return (it used to sit further down,
   * beside the plan derivations). The save/skip handlers close over
   * `currentAgentId` for the once-per-agent dialog gate, and a binding
   * declared after an early return is in the temporal dead zone for that
   * render — safe today only because neither handler can fire while the
   * loading placeholder is up. Hoisting removes that trap. Both are pure
   * reads of agentCtx / sessionStorage, so evaluating them earlier is free.
   */
  const agentObj = agentCtx?.agent;
  const currentAgentId =
    agentObj?.agent_id ||
    agentCtx?.agentId ||
    sessionStorage.getItem("SelectAgentId") ||
    "";

  if (!ready) {
    return (
      <div style={{ padding: 40, textAlign: "center", color: "#6b7280" }}>
        Loading triage rules…
      </div>
    );
  }

  /*
   * Assign-number-only fast path.
   *
   * When the user came in from FastAgentDetails' Assign Number CTA
   * (sessionStorage flag set there), the SophiaReadyDialog is
   * pointless — its two CTAs both ultimately route to
   * /fast-agent-detail in this mode (the dashboard return is set
   * inside finishToContactSync's assign-only branch). Showing the
   * "We still have to setup below — Calendar Sync / Spam Filtering"
   * pitch to a user who's just adding a number to an existing agent
   * is also wrong framing — they already finished onboarding.
   *
   * So: in assign-only mode we skip the dialog and call onNext
   * directly. GoLiveWizard.goNext → finishToContactSync → reads the
   * same flag → navigates to /fast-agent-detail. Clean and silent.
   */
  const isAssignNumberOnly = () => {
    try {
      return sessionStorage.getItem("goLiveWizard.assignNumberOnly") === "1";
    } catch (_e) {
      return false;
    }
  };

  // Setup Score: triageRulesSaved (10 pts). CallTransfer persists rows
  // before invoking onWizardNext. Then we OPEN the dialog instead of
  // immediately advancing — the dialog drives the final navigation
  // (add-on cards / Get Add-Ons → intro screens, Go to Dashboard →
  // /fast-agent-detail).
  /*
   * Advance past Triage — via the dialog on the FIRST pass, silently on every
   * later one.
   *
   * `saved` = did the user save rules (true) or skip (false); it drives the
   * dialog's checklist.
   *
   * The silent branch must take the SAME continuation the dialog's Continue
   * button would have taken — NOT `onNext()`.
   *
   * `onNext` is the host wizard's goNext. On GoLive's last step that runs
   * calenderCheck and, when the calendar is not connected, shows GoogleAuthStep
   * ("Let your agent work with Google") instead of the contact-sync intro. The
   * dialog's Continue never does that under `useAddonIntro` — it calls
   * handleAddonExitTo(["contact","calendar"]) and lands on /screen-your-calls.
   * Routing the silent path through onNext therefore sent second-time users to
   * the wrong screen. Branch exactly as the dialog does.
   */
  const openReadyDialogOrAdvance = (saved) => {
    setTriageSaved(saved);
    try {
      sessionStorage.setItem(TRIAGE_SAVED_FLAG, saved ? "1" : "0");
    } catch (_e) {
      /* private mode — the reopen path just falls back to live state */
    }

    if (hasSeenReadyDialog(currentAgentId)) {
      if (useAddonIntro) {
        handleAddonExitTo(["contact", "calendar"]);
      } else if (typeof onNext === "function") {
        Promise.resolve(onNext()).catch((err) =>
          console.error("[TriageStep] silent continue threw:", err),
        );
      }
      return;
    }

    markReadyDialogSeen(currentAgentId);
    setDialogOpen(true);
  };

  const handleSave = (rulesCount) => {
    markSetupStep(
      "triageRulesSaved",
      agentCtx?.agentId || agentCtx?.agent?.agent_id,
    );
    trackEvent("call_triage_saved", {
      ...(typeof rulesCount === "number" ? { rules_count: rulesCount } : {}),
    });
    if (isAssignNumberOnly()) {
      // Skip the dialog — finishToContactSync handles the redirect.
      if (typeof onNext === "function") {
        Promise.resolve(onNext()).catch((err) =>
          console.error("[TriageStep] assign-only continue threw:", err),
        );
      }
      return;
    }
    openReadyDialogOrAdvance(true);
  };

  // Skip path — same dialog as Save & Continue per spec ("dono pe").
  // We don't fire triageRulesSaved here because the user is
  // explicitly skipping the rule-save.
  const handleSkip = () => {
    trackEvent("call_triage_skipped");
    if (isAssignNumberOnly()) {
      // Mirror the assign-only fast path in handleSave.
      if (typeof onNext === "function") {
        Promise.resolve(onNext()).catch((err) =>
          console.error("[TriageStep] assign-only skip threw:", err),
        );
      }
      return;
    }
    openReadyDialogOrAdvance(false);
  };

  /*
   * Legacy "Continue Setup" path (PostPurchase host, useAddonIntro=false).
   * onNext (= host goNext) is async on the last step: it runs
   * calenderCheck and decides between routing to /contact-sync vs
   * swapping the wizard to inline GoogleAuthStep / ContactSync. We keep
   * the dialog open while that decision resolves, then close it after.
   */
  const handleDialogContinue = async () => {
    if (typeof onNext === "function") {
      try {
        await onNext();
      } catch (err) {
        console.error("[TriageStep] continue flow threw:", err);
      }
    }
    setDialogOpen(false);
  };

  const handleDialogDashboard = () => {
    setDialogOpen(false);
    /*
     * Mirror goNext's terminal cleanup. We clear BOTH wizard resume
     * keys defensively because TriageStep is shared between
     * GoLiveWizard and PostPurchaseSetup — without the second
     * `removeItem`, a PostPurchase user dropping to dashboard from
     * the dialog would still have `postPurchaseSetup.lastStep`
     * lingering, and a refresh on /post-purchase-setup would resume
     * them on Triage instead of treating the wizard as complete.
     */
    try {
      localStorage.removeItem("goLiveWizard.lastStep");
      localStorage.removeItem("postPurchaseSetup.lastStep");
      localStorage.setItem("onboardComplete", "true");
    } catch { }
    // Abandon any add-on intro flow state so a later /contact-sync visit
    // doesn't wrongly think it's mid-sequence.
    if (useAddonIntro) {
      navigate(finishAddonFlow(), { replace: true });
      return;
    }
    clearAddonFlow();
    navigate("/fast-agent-detail", { replace: true });
  };

  /*
   * "Improve {agent}" add-on cards (and "Get Add-Ons") in the
   * SophiaReadyDialog launch an ordered SEQUENCE through the full-screen
   * intro nudges. BOTH the primary action (Allow / Connect) AND Skip on
   * each screen advance to the NEXT screen; the dashboard comes only
   * after every screen has been shown — no ping-pong (see
   * lib/addonIntroFlow.js):
   *   - Spam filtering / Get Add-Ons → contact → calendar → dashboard
   *   - Connect calendar             → calendar → contact → dashboard
   *
   * These are the FORWARD onboarding path, not a reversible side-trip —
   * so we clear the wizard resume keys and mark onboarding complete
   * (same cleanup as "Go to Dashboard") to stop the wizard resuming on
   * Triage if the user later navigates back. agentId / userId are
   * stashed by startAddonFlow so downstream routes keep context.
   */
  const handleAddonExitTo = (sequence) => {
    setDialogOpen(false);
    /*
     * `onboardComplete` and the per-run finish route (/setup-ready vs the
     * dashboard) both live in beginAddonSequence now. They were inline here
     * until GoLiveWizard gained a second way into the sequence — it hands off
     * when a /setup-ready visit hops over an already-configured Triage — and
     * two copies of "where does this run finish?" would have drifted apart.
     * Order of side effects is unchanged: mark complete, resolve the finish
     * route, start the flow, navigate. `goLiveWizard.lastStep` is deliberately
     * NOT cleared, so Back from the first add-on screen resumes the wizard at
     * Triage and re-pops this dialog.
     */
    navigate(beginAddonSequence(sequence, location.search));
  };

  /*
   * Legacy add-on side-trip (PostPurchase host, useAddonIntro=false).
   * Tapping a card is a reversible side-trip — we DON'T clear the
   * wizard resume key, we push (no replace) so Back returns here, and
   * set a flag so the dialog auto-reopens on return (see the reopen
   * effect above). Routes: spam filtering -> /contacts, calendar ->
   * /connect-calender2.
   */
  const handleDialogExitTo = (path) => {
    setDialogOpen(false);
    try {
      sessionStorage.setItem("goLive.reopenReadyDialog", "1");
      if (path === "/connect-calender2") {
        sessionStorage.setItem("goLive.calendarAddonReturn", "1");
      }
    } catch { /* private mode — return-to-dialog just won't auto-fire */ }
    navigate(path); // push, NOT replace → Back lands back on go-live
  };

  // Same key strategy as CallRoutingStep — re-mount CallTransfer when
  // agent / business identity changes so we don't ship stale state.
  const bizPhoneForKey = (() => {
    const kb = agentCtx?.business?.knowledge_base_texts;
    try {
      const parsed = typeof kb === "string" ? JSON.parse(kb) : kb || {};
      return parsed?.phone || "";
    } catch {
      return "";
    }
  })();
  const uniqueKey = `${agentCtx?.agentId}-${agentCtx?.agent?.llmId}-${agentCtx?.business?.webUrl || ""
    }-${bizPhoneForKey}`;

  /*
   * Category-specific triage defaults (REXPT.pdf §2 Phase 4 Screen 13
   * Step 3). We resolve the rule set from `business.businessType`, which
   * is the canonical category column — falling back through
   * `business.category` and `agent.businessType` so older / partially-
   * migrated records still get sensible defaults rather than the generic
   * fallback. CallTransfer seeds these only ONCE on the first render
   * with zero rules; subsequent visits never re-seed.
   *
   * GoLive-only: PostPurchase's CallRoutingStep doesn't pass this prop
   * and gets the existing single-row "Service" behaviour.
   */
  const businessType =
    agentCtx?.business?.businessType ||
    agentCtx?.business?.category ||
    agentCtx?.agent?.businessType ||
    "";
  const categoryRows = getTriageDefaultsForCategory(businessType);

  const agentName = agentCtx?.agent?.agentName || "Sophia";
  // Agent ka apna avatar — dialog me generic tasveer ki jagah yahi dikhta hai.
  const agentAvatar =
    agentCtx?.agent?.avatar || agentCtx?.agent?.agentAvatar || "/svg/receptionist_profile.png";

  /*
   * Heading per Figma — TWO lines:
   *   1. "Tell {agentName}" (light/regular, agent name purple)
   *   2. "When Calls Transfer" (bold)
   *
   * Earlier this was a single-line "Tell {agent} When to Transfer
   * Calls" in 22px which read smaller than the AssignNumber screen
   * heading. New copy + sizes match the Figma's hero treatment so
   * Triage feels like a continuation of the Step 1 visual rhythm.
   */
  const customHeader = (
    <div className={styles.specHeader}>
      <h1 className={styles.headline}>
        {/* Tell <span className={styles.headlinePurple}>{agentName}</span> */}
        Tell {agentName}, When
      </h1>
      <h1 className={styles.headlineBold}> to Transfer Calls</h1>

      <p className={styles.bodyText}>
        Some calls need a human. Tell {agentName} <br/> who to call for each situation:
      </p>
    </div>
  );

  // Business name preference order — matches the same fallback chain
  // /immediate-owner-ship uses for the personalization copy.
  const businessName =
    agentCtx?.business?.business_name ||
    agentCtx?.business?.businessName ||
    agentCtx?.business?.companyName ||
    agentCtx?.business?.name ||
    "your business";

  /* agentObj / currentAgentId are declared above the `!ready` early return. */

  let metadataObj = {};
  try {
    const metaRaw =
      agentObj?.latestSubscription?.metadata ||
      agentObj?.subscription_Details ||
      sessionStorage.getItem("selectedPlanData");
    metadataObj =
      typeof metaRaw === "string" ? JSON.parse(metaRaw) : metaRaw || {};
  } catch {
    metadataObj = {};
  }

  const selectedPlanSession = (sessionStorage.getItem("selectedPlan") || "").toLowerCase();
  const isPaygActiveSession = sessionStorage.getItem("goLiveWizard.paygActive");
  const isTrialActiveSession = sessionStorage.getItem("goLiveWizard.trialActive");
  const isTrialSession = sessionStorage.getItem("isTrial");
  const goLiveVariant = localStorage.getItem("goLiveActiveVariant");

  const isPayg =
    agentObj?.agentPlan === "Pay-As-You-Go" ||
    agentObj?.agentPlan === "payg" ||
    agentObj?.is_payg === 1 ||
    agentObj?.isPayg === true ||
    (isPaygActiveSession && (isPaygActiveSession === currentAgentId || isPaygActiveSession === "true")) ||
    selectedPlanSession === "payg" ||
    selectedPlanSession === "pay-as-you-go";

  const isTrial =
    !isPayg &&
    (metadataObj?.trailPack === "yes" ||
      agentObj?.latestSubscription?.status === "trialing" ||
      agentObj?.agentPlan?.toLowerCase() === "trial" ||
      agentObj?.agentPlan?.toLowerCase()?.includes("trial") ||
      selectedPlanSession.includes("trial") ||
      selectedPlanSession.includes("21-day") ||
      isTrialActiveSession === currentAgentId ||
      isTrialActiveSession === "true" ||
      isTrialSession === "1" ||
      isTrialSession === "true" ||
      goLiveVariant === "trial" ||
      agentObj?.planMinutes === 9000 ||
      agentObj?.planMinutes === 150 ||
      agentObj?.mins_left === 9000 ||
      agentObj?.mins_left === 150 ||
      (agentObj?.agentPlan?.toLowerCase() === "starter" &&
        ((agentObj?.planMinutes && agentObj?.planMinutes <= 9000) ||
          (agentObj?.mins_left && agentObj?.mins_left <= 9000))));

  const trialMinutes =
    Number(metadataObj?.trial_minutes) ||
    (agentObj?.planMinutes
      ? agentObj.planMinutes > 500
        ? Math.round(agentObj.planMinutes / 60)
        : agentObj.planMinutes
      : 150) ||
    150;
  const trialDays = Number(metadataObj?.trial_days) || 21;

  /*
   * Dialog checklist — real state, not three hardcoded green ticks.
   *
   * Each row uses the SAME rule /setup-ready uses (SetUpReady.js:281-283):
   * the setup-score step OR the durable agent field. Reading only the agent
   * field was the bug — agentCtx is fetched at WIZARD mount, so it predates
   * the assign-number and forwarding steps the user just completed in this
   * very run, and forwarding showed as "not set up" seconds after being set up.
   *
   * number:     score.phoneNumberAssigned OR the voip_numbers parse (same one
   *             GoLiveWizard.hasExistingNumber() uses — the field arrives as a
   *             JSON string, a parsed array, or bare).
   * forwarding: score.callForwardingDone OR agent.callForwarding.
   * triage:     whether THIS pass saved rules or skipped them. Deliberately NOT
   *             from the score: markSetupStep() is fire-and-forget, so a
   *             re-read would race the write and could show a just-saved step
   *             as pending. The click is the authoritative answer here.
   */
  const scoreDone = (step) => !!scoreSteps?.[step]?.done;

  const numberAssigned = scoreDone("phoneNumberAssigned") || (() => {
    const raw = agentObj?.voip_numbers;
    if (!raw) return false;
    try {
      const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
      const first = Array.isArray(parsed) ? parsed[0] : parsed;
      return !!first;
    } catch {
      // Bare non-JSON string (e.g. "+17712228057") is still a real number.
      return typeof raw === "string" ? !!raw.trim() : false;
    }
  })();

  const forwardingActive =
    scoreDone("callForwardingDone") || !!agentObj?.callForwarding;

  const readyItems = [
    {
      key: "number",
      label: numberAssigned
        ? "Business number assigned"
        : "Business number not assigned",
      done: numberAssigned,
    },
    {
      key: "forwarding",
      label: forwardingActive
        ? "Call forwarding active"
        : "Call forwarding not set up",
      done: forwardingActive,
    },
    {
      key: "triage",
      label: triageSaved ? "Call triage set" : "Call triage skipped",
      done: !!triageSaved,
    },
  ];

  return (
    <div className={styles.triageWrapper} style={{ marginTop: 20 }}>
      <CallTransfer
        key={uniqueKey}
        wizardMode={true}
        customHeader={customHeader}
        seedCategoryRows={categoryRows}
        onWizardNext={handleSave}
        onWizardBack={onBack}
        businessDetails={agentCtx?.business}
      />

      {/*
         Skip link — Figma Screen 11 anchors it directly below the
         Save & Continue CTA. CallTransfer's wizard CTA now renders
         as a FULL-WIDTH white shelf at bottom: 0 (height ~110px,
         zIndex 60) with the button up top and a 56px reserve area
         below for THIS Skip link. We sit at bottom: 16 inside that
         reserve, with zIndex 70 so we layer ABOVE the shelf BG
         (otherwise the shelf's white background would obscure the
         link). Both this Skip and the Save & Continue route to the
         SAME SophiaReadyDialog per spec ("dono pe ek dialog").
      */}
      <div
        style={{
          position: "fixed",
          /* var(--wizard-cta-lift, 0px) keeps the skip link inside
             CallTransfer's reserve area when the host wizard lifts
             CTAs up for a footer dot bar (PostPurchase). */
          bottom: "calc(16px + var(--wizard-cta-lift, 0px))",
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 70,
          display: "flex",
          justifyContent: "center",
        }}
      >
        <button
          type="button"
          onClick={handleSkip}
          style={{
            background: "transparent",
            border: "none",
            padding: "6px 12px",
            fontSize: 14,
            fontWeight: 400,
            color: "#6b7280",
            textDecoration: "underline",
            textUnderlineOffset: 3,
            cursor: "pointer",
          }}
        >
          Skip
        </button>
      </div>

      <SophiaReadyDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        readyItems={readyItems}
        businessName={businessName}
        agentName={agentName}
        agentAvatar={agentAvatar}
        calendarConnected={calendarConnected}
        isTrial={isTrial}
        trialMinutes={trialMinutes}
        trialDays={trialDays}
        isPayg={isPayg}
        freeMinutes={50}
        onContinue={
          useAddonIntro
            ? () => handleAddonExitTo(["contact", "calendar"])
            : handleDialogContinue
        }
        onGoDashboard={handleDialogDashboard}
        onSpamFilter={
          useAddonIntro
            ? () => handleAddonExitTo(["contact", "calendar"])
            : () => handleDialogExitTo("/contacts")
        }
        onConnectCalendar={
          useAddonIntro
            ? () => handleAddonExitTo(["calendar", "contact"])
            : () => handleDialogExitTo("/connect-calender2")
        }
      />
    </div>
  );
}
